import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { ROUTES } from '../routes';

interface GoalCompletionModalProps {
  goalId: string;
  onClose: () => void;
}

interface GoalSummary {
  title: string;
  taskCount: number;
  eventCount: number;
  noteCount: number;
  subGoalCount: number;
}

export default function GoalCompletionModal({ goalId, onClose }: GoalCompletionModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { goals, tasks, events, notes } = useStore();
  const [summary, setSummary] = useState<GoalSummary | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const goal = goals.find((g) => g.id === goalId);
    if (!goal) return;

    const linkedTasks = tasks.filter((t) => t.goalId === goalId);
    const linkedEvents = events.filter((e) => e.goalId === goalId);
    const linkedNotes = notes.filter((n) => n.goalId === goalId);
    const subGoals = goals.filter((g) => g.parentGoalId === goalId);

    setSummary({
      title: goal.title,
      taskCount: linkedTasks.length,
      eventCount: linkedEvents.length,
      noteCount: linkedNotes.length,
      subGoalCount: subGoals.length,
    });

    setTimeout(() => setVisible(true), 50);
  }, [goalId, goals, tasks, events, notes]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const handleViewTimeline = () => {
    handleClose();
    setTimeout(() => navigate(ROUTES.timeline), 250);
  };

  if (!summary) return null;

  const badges = [
    summary.taskCount > 0 && {
      label: t(
        summary.taskCount > 1 ? 'goal_completion.task_count_plural' : 'goal_completion.task_count',
        { count: summary.taskCount }
      ),
      color: '#a78bfa',
      icon: 'task',
    },
    summary.eventCount > 0 && {
      label: t(
        summary.eventCount > 1
          ? 'goal_completion.event_count_plural'
          : 'goal_completion.event_count',
        { count: summary.eventCount }
      ),
      color: '#fb923c',
      icon: 'event',
    },
    summary.noteCount > 0 && {
      label: t(
        summary.noteCount > 1 ? 'goal_completion.note_count_plural' : 'goal_completion.note_count',
        { count: summary.noteCount }
      ),
      color: '#34d399',
      icon: 'note',
    },
    summary.subGoalCount > 0 && {
      label: t(
        summary.subGoalCount > 1
          ? 'goal_completion.sub_goal_count_plural'
          : 'goal_completion.sub_goal_count',
        { count: summary.subGoalCount }
      ),
      color: '#60a5fa',
      icon: 'subgoal',
    },
  ].filter(Boolean) as { label: string; color: string; icon: string }[];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(10,10,14,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: visible ? 1 : 0,
        transition: 'opacity 200ms ease',
      }}
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#161b22',
          border: '0.5px solid rgba(167,139,250,0.4)',
          borderRadius: 16,
          width: '100%',
          maxWidth: 370,
          overflow: 'hidden',
          transform: visible ? 'translateY(0)' : 'translateY(12px)',
          transition: 'transform 200ms ease',
        }}
      >
        <div
          style={{
            padding: '32px 28px 24px',
            textAlign: 'center',
            background:
              'linear-gradient(160deg, rgba(139,92,246,0.12) 0%, rgba(251,146,60,0.06) 100%)',
            borderBottom: '0.5px solid rgba(255,255,255,0.06)',
            position: 'relative',
          }}
        >
          <ConfettiDots />

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'rgba(251,146,60,0.12)',
              border: '0.5px solid rgba(251,146,60,0.4)',
              marginBottom: 14,
              animation: 'vb-float 3s ease-in-out infinite',
            }}
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fb923c"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 9H4.5a2.5 2.5 0 010-5H6" />
              <path d="M18 9h1.5a2.5 2.5 0 000-5H18" />
              <path d="M4 22h16" />
              <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
              <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
              <path d="M18 2H6v7a6 6 0 0012 0V2z" />
            </svg>
          </div>

          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: '#fb923c',
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            {t('goal_completion.badge')}
          </div>
          <div
            style={{
              fontSize: 19,
              fontWeight: 500,
              color: '#f5f5f5',
              marginBottom: 6,
              lineHeight: 1.25,
            }}
          >
            {t('goal_completion.heading')}
          </div>
          <div style={{ marginBottom: 10 }}>
            <span
              style={{
                display: 'inline-block',
                fontSize: 13,
                fontWeight: 500,
                color: '#c4b5fd',
                background: 'rgba(139,92,246,0.12)',
                border: '0.5px solid rgba(139,92,246,0.3)',
                borderRadius: 20,
                padding: '3px 12px',
              }}
            >
              {summary.title}
            </span>
          </div>
          <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.55 }}>
            {t('goal_completion.message')}
          </div>
        </div>

        <div style={{ padding: '16px 28px 22px' }}>
          {badges.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
              {badges.map((badge) => (
                <span
                  key={badge.icon}
                  style={{
                    fontSize: 11,
                    color: '#7c7c8a',
                    background: 'rgba(255,255,255,0.04)',
                    border: '0.5px solid rgba(255,255,255,0.08)',
                    borderRadius: 20,
                    padding: '4px 10px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <BadgeIcon type={badge.icon} color={badge.color} />
                  {badge.label}
                </span>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={handleClose}
              style={{
                fontSize: 13,
                background: 'none',
                border: 'none',
                color: '#6b7280',
                padding: '8px 12px',
                cursor: 'pointer',
                borderRadius: 8,
              }}
            >
              {t('common.close')}
            </button>
            <button
              onClick={handleViewTimeline}
              style={{
                fontSize: 13,
                fontWeight: 500,
                background: 'rgba(251,146,60,0.15)',
                border: '0.5px solid rgba(251,146,60,0.45)',
                color: '#fdba74',
                borderRadius: 8,
                padding: '8px 20px',
                cursor: 'pointer',
              }}
            >
              {t('goal_completion.see_timeline')}
            </button>
          </div>
        </div>

        <style>{`
          @keyframes vb-float {
            0%,100% { transform: translateY(0px); }
            50% { transform: translateY(-4px); }
          }
          @keyframes vb-pop {
            0% { opacity: 0; transform: scale(0) translate(0,0); }
            40% { opacity: 1; }
            100% { opacity: 0; transform: scale(1) translate(var(--tx), var(--ty)); }
          }
        `}</style>
      </div>
    </div>
  );
}

function ConfettiDots() {
  const colors = ['#a78bfa', '#fb923c', '#f472b6', '#34d399', '#fbbf24', '#60a5fa'];
  const dots = [
    { x: 20, y: 10 },
    { x: 50, y: 5 },
    { x: 80, y: 15 },
    { x: 110, y: 5 },
    { x: 260, y: 8 },
    { x: 300, y: 12 },
    { x: 330, y: 5 },
    { x: 350, y: 20 },
    { x: 30, y: 160 },
    { x: 340, y: 170 },
    { x: 15, y: 80 },
    { x: 355, y: 90 },
  ];
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {dots.map((pos, i) => {
        const size = 4 + (i % 3) * 2;
        const tx = ((i % 5) - 2) * 12;
        const ty = ((i % 3) - 1) * 12;
        const delay = (i * 0.07).toFixed(2);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: size,
              height: size,
              borderRadius: '50%',
              background: colors[i % colors.length],
              left: pos.x,
              top: pos.y,
              opacity: 0,
              ['--tx' as string]: `${tx}px`,
              ['--ty' as string]: `${ty}px`,
              animation: `vb-pop ${0.5 + (i % 3) * 0.15}s ease-out ${delay}s forwards`,
            }}
          />
        );
      })}
    </div>
  );
}

function BadgeIcon({ type, color }: { type: string; color: string }) {
  const props = {
    width: 11,
    height: 11,
    fill: 'none',
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (type === 'task')
    return (
      <svg viewBox="0 0 24 24" {...props}>
        <polyline points="9 11 12 14 22 4" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    );
  if (type === 'event')
    return (
      <svg viewBox="0 0 24 24" {...props}>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  if (type === 'note')
    return (
      <svg viewBox="0 0 24 24" {...props}>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    );
  return (
    <svg viewBox="0 0 24 24" {...props}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}
